import { useCallback, useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useGetBusinesses } from "@/business/query";
import { useGetMyUser } from "@/user/query";
import BusinessContext from "./business-context";
import {
  getTenantSelectableBusinesses,
  getValidTenantBusinessId,
} from "./utils";

const STORAGE_KEY = "selectedBusinessId";

const BusinessProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const navigate = useNavigate();
  const location = useLocation();

  // Fetch user and businesses
  const {
    data: user,
    isLoading: isUserLoading,
    isError: isUserError,
  } = useGetMyUser();
  const {
    data: allBusinesses = [],
    isLoading: isBusinessesLoading,
    isError: isBusinessesError,
  } = useGetBusinesses();

  // State for selected business (only relevant for tenant users)
  const [storedBusinessId, setStoredBusinessId] = useState<string | null>(
    localStorage.getItem(STORAGE_KEY),
  );

  // Determine if user is a tenant user
  const isTenantUser = user?.parentable_type === "Tenant";

  // Churned businesses remain available to admin-only flows that consume the
  // businesses query directly, but they are not valid tenant selections.
  const businesses = useMemo(
    () =>
      isTenantUser
        ? getTenantSelectableBusinesses(allBusinesses)
        : allBusinesses,
    [allBusinesses, isTenantUser],
  );

  // Select a business and update URL if needed
  const selectBusiness = useCallback(
    (businessId: string) => {
      setStoredBusinessId(businessId);
      localStorage.setItem(STORAGE_KEY, businessId);

      // If we're in the business settings area, update the URL
      if (location.pathname.includes("/business-settings/")) {
        const currentPath = location.pathname;
        const pathParts = currentPath.split("/");
        const currentBusinessIndex =
          pathParts.findIndex((part) => part === "business-settings") + 1;

        if (currentBusinessIndex > 0) {
          pathParts[currentBusinessIndex] = businessId;
          const newPath = pathParts.join("/");
          if (newPath !== currentPath) {
            navigate(newPath + location.search);
          }
        }
      }
    },
    [navigate, location],
  );

  const selectedBusinessId = isTenantUser
    ? getValidTenantBusinessId(businesses, storedBusinessId)
    : storedBusinessId;

  // Determine the effective business ID
  // - For tenant users: use the selected business
  // - For business users: use their parentable_id
  const businessId = isTenantUser
    ? selectedBusinessId
    : user?.parentable_id || null;

  // Compute the selected business object
  const selectedBusiness = businesses.find((b) => b.id === businessId);

  // Determine if the selected business is spiff-only
  const isSpiffOnly = selectedBusiness?.is_spiff_only ?? false;

  // Clear stored business selection when user type changes from tenant to business user
  useEffect(() => {
    if (!user) return;

    if (!isTenantUser && storedBusinessId) {
      // For business users, clear any stored selection
      setStoredBusinessId(null);
      localStorage.removeItem(STORAGE_KEY);
    }
  }, [user, isTenantUser, storedBusinessId]);

  // Ensure tenant users have a valid business selected
  useEffect(() => {
    if (
      !user ||
      !isTenantUser ||
      !selectedBusinessId ||
      selectedBusinessId === storedBusinessId
    ) {
      return;
    }

    // Persist the synchronously derived fallback selection.
    selectBusiness(selectedBusinessId);
  }, [
    user,
    isTenantUser,
    selectedBusinessId,
    storedBusinessId,
    selectBusiness,
  ]);

  // Sync URL business ID with selected business ID for tenant users
  useEffect(() => {
    if (!isTenantUser || !businessId || isBusinessesLoading) return;

    const pathMatch = location.pathname.match(/\/business-settings\/([^/]+)/);
    if (pathMatch && pathMatch[1] !== businessId) {
      selectBusiness(businessId);
    }
  }, [
    location.pathname,
    businessId,
    isTenantUser,
    isBusinessesLoading,
    selectBusiness,
  ]);

  return (
    <BusinessContext.Provider
      value={{
        businesses,
        selectedBusinessId,
        businessId,
        isLoading: isUserLoading || isBusinessesLoading,
        isError: isUserError || isBusinessesError,
        selectBusiness,
        isTenantUser,
        isSpiffOnly,
        selectedBusiness,
      }}
    >
      {children}
    </BusinessContext.Provider>
  );
};

export default BusinessProvider;
